import type { AxiosRequestConfig, AxiosResponse } from "axios";
import axios from "axios";

const protocol = window.location.protocol;
const hostname = window.location.hostname;
const apiBaseUrl =
  import.meta.env.VITE_APP_API_URL || `${protocol}//${hostname}:9000`;

export const apiClient = axios.create({
  baseURL: apiBaseUrl,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Variable to store the token getter function from context
let getAccessToken: (() => string | undefined) | null = null;

/**
 * Register a function to retrieve the access token
 * This function will be called by the Axios request interceptor on every request
 * @param getter Function that returns the current access token
 */
export const setTokenGetter = (getter: () => string | undefined) => {
  getAccessToken = getter;
};

// Request interceptor: Inject JWT access token
apiClient.interceptors.request.use(
  (config) => {
    const accessToken = getAccessToken?.();
    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error) => {
    if (
      error.response?.status === 401 &&
      !window.location.href.includes("/login")
    ) {
      window.location.pathname = "/maintenance/500";
    }
    return Promise.reject(
      (error.response && error.response.data) || "Wrong Services",
    );
  },
);

export const api = {
  get: <T = unknown>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> => apiClient.get(url, config),

  post: <T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> => apiClient.post(url, data, config),

  put: <T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> => apiClient.put(url, data, config),

  patch: <T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> => apiClient.patch(url, data, config),

  delete: <T = unknown>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> => apiClient.delete(url, config),
};
